import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, InputNumber } from 'antd';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { getSustainabilityIndexByMonthlyRateAndDeposit } from 'utils/prescoringTool';
import './styles.scss';
import messages from './messages';
import configs from 'configs';
import {FormattedMessage} from "react-intl";

import { default as InputSlider } from '../Input/Slider/';

const prescoringColors = [
  '#f5222d',
  '#ffbf3f',
  '#2ec58d',
]

class Fee extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
      selectedFees: this.props.selectedFees,
      monthlyRate: this.props.monthlyRate,
    };

    this.sliderRef = React.createRef();
  }

  componentDidUpdate = (prevProps) => {
    const { monthlyRate, selectedFees } = this.props;

    if (prevProps.selectedFees.overCommission !== selectedFees.overCommission) {
      this.setState({selectedFees})
    }

    if (
      (prevProps.selectedFees.finalMonthlyRate !== selectedFees.finalMonthlyRate)
      ||
      (prevProps.monthlyRate !== monthlyRate)
    ) {
      this.setState({
        selectedFees: {
          ...selectedFees,
          finalMonthlyRate: selectedFees.finalMonthlyRate
        },
        monthlyRate
      })
    }
  }

  toggle = () => {
    this.setState(prevState => ({
      show: !prevState.show,
    }));
  };

  getFinalPotentialProfitValueByFinalMonthlyRate = finalMonthlyRate =>
    this.props.item -
      (this.props.monthlyRate - finalMonthlyRate) / (this.getTaxMultiplicationFactor() / this.getDurationValueForStepCalculation())

  onOverwriteMonthlyRateHandler = value => {
    let { selectedFees, offerFees, item, monthlyRate, onUpdateFees } = this.props

    if (typeof selectedFees.__altered !== 'undefined') {
      selectedFees = selectedFees.toJS();
    }

    const finalMonthlyRate = value + (monthlyRate - parseInt(monthlyRate));
    let toAddToTotalPrice;
    let extraCommissionToIdeal;
    let baseCommissionToIdeal;

    const finalPotentialProfitValue = this.getFinalPotentialProfitValueByFinalMonthlyRate(finalMonthlyRate);

    toAddToTotalPrice = this.getValueToAddToTotalPrice(finalPotentialProfitValue - item);

    extraCommissionToIdeal = Math.ceil(toAddToTotalPrice - (finalPotentialProfitValue - item));

    baseCommissionToIdeal = Math.ceil(item * (100 - offerFees.agentFee) / offerFees.agentFee);

    onUpdateFees({
      ...selectedFees,
      originalPotentialProfit: item,
      extraCommissionToIdeal,
      baseCommissionToIdeal,
      originalMonthlyRate: monthlyRate,
      overCommission: parseFloat(finalPotentialProfitValue - item).toFixed(2),
      finalPotentialProfit: finalPotentialProfitValue,
      finalMonthlyRate,
    })
  }

  onOverwriteMonthlyRateHandlerDebounced = AwesomeDebouncePromise(
    this.onOverwriteMonthlyRateHandler,
    200
  )

  getMonthlyRateLogic = commissionDelta => {
    const { extraCommission } = configs;

    if (extraCommission && extraCommission.monthlyRateLogic) {
      const monthlyRateLogicCalculation = extraCommission.monthlyRateLogic.replace('$commissionDelta', commissionDelta)
        .replace('$duration', this.getDurationValueForStepCalculation());

      return this.props.monthlyRate + Math.round(eval(monthlyRateLogicCalculation));
    }

    return this.getValueToAddToTotalPrice(commissionDelta) / this.getDurationValueForStepCalculation();
  }

  getValueToAddToTotalPrice = commissionDelta => commissionDelta / this.props.offerFees.agentFee * 100

  getValueToAddToMonthlyRate = commissionDelta => this.getMonthlyRateLogic(commissionDelta)

  onChangeOverCommissionValue = newValue => {
    const { has_extra_commission, has_over_commission, item, onUpdateFees, offerFees, monthlyRate } = this.props;

    if (has_extra_commission) {
      let finalMonthlyRate = parseFloat(monthlyRate);
      let toAddToTotalPrice;
      let extraCommissionToIdeal;
      let baseCommissionToIdeal;

      toAddToTotalPrice = this.getValueToAddToTotalPrice(newValue - item);

      if (has_over_commission) {
        finalMonthlyRate += parseFloat(this.getValueToAddToMonthlyRate(newValue - item).toFixed(2));
      } else {
        finalMonthlyRate = parseFloat(this.getValueToAddToMonthlyRate(newValue - item).toFixed(2));
      }

      if (finalMonthlyRate < this.getMinMonthlyRate()) {
        finalMonthlyRate = this.getMinMonthlyRate();
      }

      if (finalMonthlyRate > this.getMaxMonthlyRate()) {
        finalMonthlyRate = this.getMaxMonthlyRate();
      }

      extraCommissionToIdeal = Math.ceil(toAddToTotalPrice - (newValue - item));

      baseCommissionToIdeal = Math.ceil(item * (100 - offerFees.agentFee) / offerFees.agentFee);

      onUpdateFees({
        overCommission: newValue - item,
        originalPotentialProfit: item,
        finalPotentialProfit: newValue,
        extraCommissionToIdeal,
        baseCommissionToIdeal,
        finalMonthlyRate,
        originalMonthlyRate: monthlyRate,
      })
    }
  }

  getSliderMax = () => {
    const { offerFees, has_extra_commission } = this.props;

    if (has_extra_commission) {
      return offerFees.maxFeeValue ;
    }

    return 0;
  }

  getSliderMin = () => {
    const { offerFees, has_over_commission } = this.props;

    if (has_over_commission) {
      return offerFees.minFeeValue;
    }

    return 0;
  }

  getTaxMultiplicationFactor = () => {
    const { extraCommission } = configs;

    if (extraCommission && extraCommission.taxMultiplicationFactor) {
      return extraCommission.taxMultiplicationFactor;
    }

    return 1;
  }

  getMinMonthlyRate = () => {
    const { has_under_commission, monthlyRate, duration, item } = this.props;

    if (has_under_commission) {
      return Math.floor(
        monthlyRate - Math.ceil(
          ((item * 2) / duration) * this.getTaxMultiplicationFactor()
        )
      );
    }

    return monthlyRate + parseInt(this.getValueToAddToMonthlyRate(this.getSliderMin() - item));
  }

  getMaxMonthlyRate = () =>
    this.props.has_under_commission
    ? parseInt(this.getValueToAddToMonthlyRate(this.getSliderMax() - this.props.item))
    : this.props.monthlyRate + Math.ceil(this.getValueToAddToMonthlyRate(this.getSliderMax() - this.props.item));

  getDurationValueForStepCalculation = () => {
    const { duration } = this.props;
    const { extraCommission } = configs;

    if (extraCommission) {
      if (extraCommission.multiplicationFactor) {
        return duration * extraCommission.multiplicationFactor;
      } else if (extraCommission.dividingFactor) {
        return duration / extraCommission.dividingFactor;
      }
    }

    return duration;
  }

  getSliderSteps = () => {
    const { item, duration, selectedFees, monthlyRate } = this.props;

    const sliderMin = (this.getSliderMin());
    const sliderMax = (this.getSliderMax());

    let prevCeilValue = parseInt(sliderMin);

    let steps = {};

    // under commission
    if (item > sliderMin) {

      for (let price = monthlyRate; price > this.getMinMonthlyRate(); price--) {
        const step = item - ((monthlyRate - price) / (this.getTaxMultiplicationFactor() / this.getDurationValueForStepCalculation()));

        let style = {
          display: 'none'
        }

        if (selectedFees.overCommission === item - step) {
          style = {
            color: this.getStepColor(step)
          }
        }

        steps[parseFloat(step.toFixed(2))] = {
          style
        };
      }
    }

    // over commission
    if (item < sliderMax) {
      prevCeilValue = Math.ceil(sliderMin);

      for (let step = item; step < sliderMax; step += duration) {
        const toAdd = this.getValueToAddToMonthlyRate(step - sliderMin);
        const ceilValue = Math.ceil(toAdd);

        if (prevCeilValue !== ceilValue) {
          let style = {
            display: 'none'
          }

          if (selectedFees.overCommission === item - step) {
            style = {
              color: this.getStepColor(step)
            }
          }

          steps[step] = {
            style
          };

          prevCeilValue = ceilValue;
        }
      }

      if (!steps[sliderMax]) {
        steps[sliderMax] = {
          style: {
            display: 'none'
          }
        };
      }
    }

    return steps;
  }

  getStepColor = stepValue => {
    const { monthlyRate, monthlyRateMax, depositRequired, deposit, thresholdMonthlyRateMax, item } = this.props;
    let finalMonthlyRate = monthlyRate;

    if (monthlyRateMax > 0) {
      finalMonthlyRate += Math.ceil(this.getValueToAddToMonthlyRate(stepValue - item));

      const sustainabilityIndexStep = getSustainabilityIndexByMonthlyRateAndDeposit(monthlyRateMax, finalMonthlyRate, depositRequired, deposit || 0, thresholdMonthlyRateMax);

      return prescoringColors[sustainabilityIndexStep];
    } else if (stepValue === this.getSliderMin() || stepValue === this.getSliderMax()) {
      return 'rgb(72, 72, 72)';
    } else {
      return 'rgb(255, 255, 255)';
    }
  }

  labelFormatter = value => `${Math.max(0, Math.round(parseFloat(value)))}€`

  sliderValue = () => {
    const { selectedFees, offerFees } = this.props;

    if (typeof selectedFees.finalPotentialProfit !== 'undefined') {
      return selectedFees.finalPotentialProfit
    }

    return offerFees.defaultFeeValue || 0;
  }

  render() {
    const { title, item, icon, iconExtra, has_extra_commission } = this.props;
    const { show, monthlyRate, selectedFees } = this.state;

    const sliderMin = Math.max(0, Math.round(
      parseFloat(
        Object.keys(this.getSliderSteps()).sort((val1, val2) => parseFloat(val1) - parseFloat(val2))[0]
      )
    ));
    const sliderMax = parseFloat(Object.keys(this.getSliderSteps()).sort((val1, val2) => parseFloat(val1) - parseFloat(val2)).slice(-1)[0]);

    const commissioneOverwritten = typeof selectedFees.finalPotentialProfit !== 'undefined' && selectedFees.finalPotentialProfit !== item;

    const baseFeeToShow = Math.round(parseFloat(item)).toLocaleString('it');

    let itemContent = `Guadagno potenziale: <strong class="font-weight-bold">${baseFeeToShow}€</strong>`;

    if (commissioneOverwritten) {
      const finalPotentialProfitToShow = typeof selectedFees.finalPotentialProfit !== 'undefined'
        ? Math.max(0, Math.round(parseFloat(selectedFees.finalPotentialProfit))).toLocaleString('it')
        : null;
      const arrowIcon = item > selectedFees.finalPotentialProfit
        ? `<i class="os-icon os-icon-arrow-down text-danger" />`
        : `<i class="os-icon os-icon-arrow-up2 text-success" />`;
      itemContent = `Guadagno potenziale: <strike class="text-muted">${baseFeeToShow}€</strike> <strong class="font-weight-bold">${finalPotentialProfitToShow}€</strong> ${arrowIcon}`
    }

    return (
      <div className='ui-list mb-3'>
        <div className={'icon'}> {icon} </div>
        <div className={'title'} onClick={this.toggle}>
          {title}
          <span
            className={
              !show ? 'float-right fa fa-fw fa-eye field-icon'
                : 'float-right fa fa-fw fa-eye-slash field-icon'}
          />
        </div>
          {!show && (
            <div
              id="show-extra-commission-cta"
              onClick={this.toggle}>
              Clicca per visualizzare il <i>Guadagno potenziale</i>
            </div>
          )}

          {show &&
            <div className="item" dangerouslySetInnerHTML={{ __html: itemContent }} />
          }

          {
            show && <>
            <small>
              <p>
                <FormattedMessage
                  {...messages.feeDisclaimer}
                />
                <br />
                <FormattedMessage
                  {...messages.feeDisclaimerDescription}
                />
              </p>
            </small>

            {has_extra_commission &&
            <>
            <hr />

            <div className="icon">{iconExtra}</div>
            <div className="title">Personalizza commissioni</div>
            <div className="over-commission-config-container">
              <Row>
                <Col>
                  <Row>
                    <Col xl={8} lg={24}>
                      Modifica importo di commissione:
                    </Col>
                    <Col xl={16} lg={24} className="text-nowrap" style={{marginTop: '-8px'}}>
                      &nbsp;
                      <span style={{color: this.getStepColor(sliderMin)}}>{sliderMin}€</span>
                      <div className="d-inline-block input-slider-container">
                      <InputSlider
                          range={false}
                          name="over_commission_offer"
                          step={null}
                          steps={this.getSliderSteps()}
                          value={this.sliderValue()}
                          onChange={this.onChangeOverCommissionValue}
                          labelFormatter={this.labelFormatter}
                          tooltipVisible
                          withStepLabel={false}
                          ref={this.sliderRef}
                        />
                      </div>
                      <span style={{color: this.getStepColor(sliderMax)}}>{Math.round(parseFloat(sliderMax))}€</span>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col md={24} className="mt-5">
                  Personalizza il canone mensile:
                  <InputNumber
                    className="d-inline-block"
                    style={{width: '100px', marginLeft: '20px'}}
                    type="number"
                    step={1}
                    disabled={this.getSliderMax() <=0}
                    value={parseInt(selectedFees.finalMonthlyRate || monthlyRate)}
                    min={parseInt(this.getMinMonthlyRate())}
                    max={parseInt(this.getMaxMonthlyRate())}
                    onChange={this.onOverwriteMonthlyRateHandlerDebounced}
                    suffix="€"
                  />
                </Col>
              </Row>
            </div>
            </>
            }
          </>
          }
      </div>
    );
  }
}

Fee.defaultProps = {
  icon: null,
  item: '',
  has_extra_commission: false,
  has_under_commission: false,
  has_over_commission: false,
};

Fee.propTypes = {
  title: PropTypes.string.isRequired,
  item: PropTypes.number.isRequired,
  has_extra_commission: PropTypes.bool.isRequired,
  has_under_commission: PropTypes.bool.isRequired,
  has_over_commission: PropTypes.bool.isRequired,
  icon: PropTypes.any,
  monthlyRate: PropTypes.any,
  monthlyRateMax: PropTypes.number,
  depositRequired: PropTypes.bool,
  deposit: PropTypes.number,
  sustainabilityIndex: PropTypes.number,
  offerFees: PropTypes.any,
  carValue: PropTypes.number,
  duration: PropTypes.number.isRequired,
};

export default Fee;
